import { useState } from "react";
import { X, Star, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api } from "../utils/apiComponent/apiClient";
import { endpoints } from "../utils/endpoints";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { useTranslation } from "../i18n";

/**
 * Modal réutilisable pour évaluer un cours (note et commentaire)
 */
export const CourseReviewModal = ({
  courseId,
  courseTitle,
  isOpen,
  onClose,
  onReviewSubmitted,
  required = false // Si true, l'utilisateur doit obligatoirement évaluer
}) => {
  const { tObj } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    // Validation : si required, il faut au moins une note ou un commentaire
    if (required && rating === 0 && !comment.trim()) {
      showErrorToast({ 
        message: tObj.courseReview.ratingOrCommentRequired?.() || 
        "Veuillez donner une note ou laisser un commentaire" 
      });
      return;
    }

    // Validation : commentaire max 2000 caractères
    if (comment.length > 2000) {
      showErrorToast({ 
        message: tObj.courseReview.commentTooLong?.() || 
        "Le commentaire ne peut pas dépasser 2000 caractères" 
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {};
      if (rating > 0) {
        payload.rating = rating;
      }
      if (comment.trim()) {
        payload.comment = comment.trim();
      }

      await api.post(endpoints.submitCourseReview(courseId), payload);
      
      showSuccessToast({ 
        message: tObj.courseReview.reviewSubmitted?.() || 
        "Votre évaluation a été enregistrée avec succès !" 
      });

      // Réinitialiser le formulaire
      setRating(0);
      setComment("");
      
      // Appeler le callback si fourni
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }

      // Fermer le modal
      onClose();
    } catch (error) {
      console.error("Erreur lors de la soumission de l'évaluation:", error);
      const errorMessage = error?.message || 
        tObj.courseReview.submitError?.() || 
        "Une erreur est survenue lors de l'enregistrement de votre évaluation";
      showErrorToast({ message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
      onClick={(e) => {
        // Si required, empêcher la fermeture en cliquant à l'extérieur
        if (!required && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl p-4 sm:p-5 md:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-5 md:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex-1 min-w-0 pr-2">
            {tObj.courseReview.title?.() || "Évaluer le cours"}
          </h2>
          {!required && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          )}
        </div>

        {/* Course Title */}
        {courseTitle && (
          <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-gray-50 rounded-lg">
            <p className="text-xs sm:text-sm text-gray-600 mb-1">
              {tObj.courseReview.courseTitle?.() || "Cours :"}
            </p>
            <p className="font-semibold text-sm sm:text-base text-gray-900 break-words">{courseTitle}</p>
          </div>
        )}

        {/* Rating Section */}
        <div className="mb-4 sm:mb-5 md:mb-6">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">
            {tObj.courseReview.ratingLabel?.() || "Note (optionnel)"}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="focus:outline-none transition-transform hover:scale-110"
                disabled={isSubmitting}
              >
                <Star
                  className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 ${
                    star <= (hoveredRating || rating)
                      ? "text-yellow-400 fill-yellow-400"
                      : "text-gray-300"
                  }`}
                />
              </button>
            ))}
            {rating > 0 && (
              <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-600">
                {rating}/5
              </span>
            )}
          </div>
        </div>

        {/* Comment Section */}
        <div className="mb-4 sm:mb-5 md:mb-6">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
            {tObj.courseReview.commentLabel?.() || "Commentaire (optionnel)"}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={tObj.courseReview.commentPlaceholder?.() || 
              "Partagez votre expérience avec ce cours..."}
            rows={4}
            maxLength={2000}
            disabled={isSubmitting}
            className="resize-none text-sm sm:text-base"
          />
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1 sm:gap-0 mt-1">
            <p className="text-xs text-gray-500">
              {tObj.courseReview.commentHint?.() || 
                "Votre commentaire sera visible par les autres étudiants"}
            </p>
            <span className={`text-xs ${
              comment.length > 2000 ? "text-red-500" : "text-gray-500"
            }`}>
              {comment.length}/2000
            </span>
          </div>
        </div>

        {/* Info Message if Required */}
        {required && (
          <div className="mb-4 sm:mb-5 md:mb-6 p-2 sm:p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs sm:text-sm text-yellow-800">
              {tObj.courseReview.requiredMessage?.() || 
                "Vous devez évaluer ce cours avant de pouvoir télécharger votre certificat."}
            </p>
          </div>
        )}

        {/* Footer - Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end">
          {!required && (
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="w-full sm:w-auto text-sm sm:text-base"
            >
              {tObj.courseReview.cancel?.() || "Annuler"}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || (required && rating === 0 && !comment.trim())}
            className="w-full sm:w-auto bg-[#203a8a] hover:bg-[#1a1f4a] text-white text-sm sm:text-base"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-1 sm:mr-2"></div>
                <span className="hidden sm:inline">{tObj.courseReview.submitting?.() || "Envoi..."}</span>
                <span className="sm:hidden">Envoi...</span>
              </>
            ) : (
              <>
                <Send className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{tObj.courseReview.submit?.() || "Envoyer l'évaluation"}</span>
                <span className="sm:hidden">Envoyer</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

